import React, { useRef } from 'react'
import { Card, Col, Row, Space, Table } from 'antd'
import PropTypes from 'prop-types'
import {
	GeneralContentWrapper,
	GeneralTitle
} from '../../components/Shared/GeneralComponents'
import './style.css'

const CvTemplate = ({
	name,
	age,
	city,
	phone,
	email,
	website,
	linkedin,
	github,
	aboutMe,
	strengths,
	skillsData,
	projects,
	workHistory,
	education,
	languages,
	availability,
	sectionsMarginTop = {}
}) => {
	const printRef = useRef()

	const columns = [
		{
			title: 'Category',
			dataIndex: 'category',
			key: 'category'
		},
		{
			title: 'Skills & Tools',
			dataIndex: 'skills',
			key: 'skills'
		}
	]

	return (
		<>
			<Space style={{ marginBottom: 20 }}>
				<button className="print-button" onClick={() => window.print()}>
					Print CV
				</button>
			</Space>
			<div ref={printRef}>
				<GeneralContentWrapper>
					<Row
						gutter={[10, 10]}
						justify="space-between"
						align="middle"
					>
						<Col>
							<Row
								gutter={[10, 10]}
								justify="start"
								align="middle"
							>
								<Col>
									<img
										src="/owner/eu.png"
										alt={name}
										style={{
											width: '100px',
											height: 'auto',
											borderRadius: '100%',
											border: '2px solid #000'
										}}
									/>
								</Col>
								<Col>
									<GeneralTitle
										text={name}
										style={{ lineHeight: 1, margin: 0 }}
									/>
									<p style={{ lineHeight: 1.5, margin: 0 }}>
										{city}, {age} years, Male
									</p>
								</Col>
							</Row>
						</Col>
						<Col style={{ textAlign: 'right' }}>
							{/* <p style={{ lineHeight: 1, margin: 0 }}>{phone} 📞</p> */}
							{/* <p style={{ lineHeight: 1.5, margin: 0 }}>{email} ✉️</p> */}
							{phone && (
								<p style={{ lineHeight: 1, margin: 0 }}>
									<a
										href={`tel:${phone}`}
										target="_blank"
										rel="noreferrer"
									>
										{phone} 📞
									</a>
								</p>
							)}
							{email && (
								<p style={{ lineHeight: 1.5, margin: 0 }}>
									<a
										href={`mailto:${email}`}
										target="_blank"
										rel="noreferrer"
									>
										{email} ✉️
									</a>
								</p>
							)}

							{website && (
								<p style={{ lineHeight: 1.5, margin: 0 }}>
									<a
										href={website}
										target="_blank"
										rel="noreferrer"
									>
										{website}
									</a>
								</p>
							)}
							{linkedin && (
								<p style={{ lineHeight: 1.5, margin: 0 }}>
									<a
										href={linkedin}
										target="_blank"
										rel="noreferrer"
									>
										{linkedin}
									</a>
								</p>
							)}
							{github && (
								<p style={{ lineHeight: 1.5, margin: 0 }}>
									<a
										href={github}
										target="_blank"
										rel="noreferrer"
									>
										{github}
									</a>
								</p>
							)}
						</Col>
					</Row>

					<Space
						direction="vertical"
						size="large"
						style={{ marginTop: sectionsMarginTop?.about || '' }}
					>
						<Card title="About Me">
							<p>{aboutMe}</p>
						</Card>

						<Card
							title="Key Strengths"
							style={{
								marginTop: sectionsMarginTop?.strengths || ''
							}}
						>
							<ul>
								{strengths.map((item, index) => (
									<li key={index}>{item}</li>
								))}
							</ul>
						</Card>

						<Card
							title="Skills & Tools"
							style={{
								marginTop: sectionsMarginTop?.skills || ''
							}}
						>
							<Table
								dataSource={skillsData}
								columns={columns}
								pagination={false}
								rowKey="category"
							/>
						</Card>

						<Card title="Advanced Projects">
							{projects.map((proj, index) => (
								<p key={index}>
									<b>{proj.title}</b>: {proj.description}
								</p>
							))}
						</Card>

						<Card
							title="Work History"
							style={{
								marginTop: sectionsMarginTop?.workHistory || ''
							}}
						>
							<Space
								direction="vertical"
								size="middle"
								style={{ width: '100%' }}
							>
								{workHistory.map((job, index) => (
									<Card
										type="inner"
										title={`${job.title} — ${job.company}`}
										extra={job.period}
										key={index}
									>
										<div
											dangerouslySetInnerHTML={{
												__html: job.description
											}}
										/>
										<p>
											<b>Tech stack:</b> {job.techStack}
										</p>
									</Card>
								))}
							</Space>
						</Card>

						<Card
							title="Education"
							style={{
								marginTop: sectionsMarginTop?.education || ''
							}}
						>
							{education.map((edu, index) => (
								<p key={index}>
									<b>{edu.title}</b>
									<br />
									{edu.institution} ({edu.period})<br />
									{edu.details}
								</p>
							))}
						</Card>

						{languages && languages.length > 0 && (
							<Card title="Languages">
								<ul>
									{languages.map((lang, index) => (
										<li key={index}>{lang}</li>
									))}
								</ul>
							</Card>
						)}

						{availability && availability.length > 0 && (
							<Card title="Availability">
								<ul>
									{availability.map((item, index) => (
										<li key={index}>{item}</li>
									))}
								</ul>
							</Card>
						)}
					</Space>
				</GeneralContentWrapper>
			</div>
		</>
	)
}

CvTemplate.propTypes = {
	name: PropTypes.string.isRequired,
	age: PropTypes.number.isRequired,
	city: PropTypes.string.isRequired,
	phone: PropTypes.string.isRequired,
	email: PropTypes.string.isRequired,
	website: PropTypes.string,
	linkedin: PropTypes.string,
	github: PropTypes.string,
	aboutMe: PropTypes.string.isRequired,
	strengths: PropTypes.arrayOf(PropTypes.string).isRequired,
	skillsData: PropTypes.arrayOf(
		PropTypes.shape({
			category: PropTypes.string.isRequired,
			skills: PropTypes.string.isRequired
		})
	).isRequired,
	projects: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			description: PropTypes.string.isRequired
		})
	).isRequired,
	workHistory: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			company: PropTypes.string.isRequired,
			period: PropTypes.string.isRequired,
			description: PropTypes.string.isRequired,
			techStack: PropTypes.string.isRequired
		})
	).isRequired,
	education: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			institution: PropTypes.string.isRequired,
			period: PropTypes.string.isRequired,
			details: PropTypes.string
		})
	).isRequired,
	languages: PropTypes.arrayOf(PropTypes.string),
	availability: PropTypes.arrayOf(PropTypes.string),
	sectionsMarginTop: PropTypes.shape({
		about: PropTypes.number,
		strengths: PropTypes.number,
		skills: PropTypes.number,
		workHistory: PropTypes.number,
		education: PropTypes.number
	})
}

export default CvTemplate
